/**
 * Since zod v4 the `refine()` and `check()` methods are not passthrough the e.g. `pick()` and `omit()` methods.
 * This function allows to create a schema that is refineable and can be used with the `pick()` and `omit()` methods.
 *
 * **Attention**: In your passed `refine` method make sure to check for the object's `shape`. Example:
 *
 * ```ts
 * const createSupportResponseMessageSchema = createRefinableSchema(
 *     () =>
 *         z.object({
 *             id: z.string(),
 *             message: z.string(),
 *             // [...]
 *         }),
 *     (schema) =>
 *         schema.refine(
 *             ({ startDate, endDate }) => {
 *                 // Check if the object has the `startDate` and `endDate` properties
 *                 if (schema.shape.startDate && schema.shape.endDate) {
 *                     return startDate <= endDate;
 *                 } else {
 *                     return true;
 *                 }
 *             },
 *             {
 *                 message: "Start date must be before end date",
 *                 path: ["startDate"],
 *             },
 *         ),
 * );
 * ```
 *
 * If you want to require additional context to your refinable schema (e.g. the `i18next` `t` function),
 * you can pass it as an argument to the `createRefinableSchema` function. Example:
 *
 * ```ts
 * const createSupportResponseMessageSchema = createRefinableSchema(
 *     ({ t }: CreateContractContext) =>
 *         z.object({
 *             id: z.string(),
 *             message: z.string(),
 *             // [...]
 *         }),
 * ```
 *
 * If you want to use this schema in a contract as an OpenAPI component, you should not use the `meta()` method in your
 * schema definition. Instead you should use the `meta()` method when you use the schema in a contract. Otherwise you will be
 * running into errors that the ID is already registered.
 *
 * ### Recursion
 *
 * If you want to use a schema in a recursive way, you can use the `recursionSchemas` option together with the `memoize` function (which
 * uses the mechanics of recursive-objects we learned in https://zod.dev/api?id=recursive-objects). As this gets too complex
 * to explain here, please refer to the [`README.md`](../../README.md) for an example.
 *
 * @see https://github.com/colinhacks/zod/discussions/4706
 * @see https://github.com/colinhacks/zod/issues/4427
 */ function createRefinableSchema({ schema, refine, recursionSchemas }) {
    const identifier = Symbol("recursionMap");
    const fn = (...args)=>{
        let middleware;
        if (typeof args[0] === "function") {
            middleware = args.shift();
        }
        let s = schema(...args);
        // CU-8699tgz5g Runtime check: Ensure the schema hasn't been refined
        // The schema parameter should only return a base ZodObject, not a refined one
        // Refinements should be done in the separate `refine` parameter
        if (s && typeof s === "object" && "_def" in s && s._def?.checks) {
            throw new Error("createRefinableSchema: Do not call .refine() within the 'schema' parameter. Move refinements to the separate 'refine' parameter instead.");
        }
        let memoizeRecursionMap = new Map();
        // Check if there is already a memoization map for recursion
        for (const arg of args){
            if (arg instanceof Map) {
                const { value } = arg.keys().next();
                if (typeof value === "symbol" && value.description === "recursionMap") {
                    args.splice(args.indexOf(arg), 1);
                    memoizeRecursionMap = arg;
                    break;
                }
            }
        }
        if (recursionSchemas) {
            const memoize = (originalFn)=>{
                const { identifier } = originalFn;
                return (...args)=>{
                    const cache = memoizeRecursionMap.get(identifier);
                    if (cache) {
                        return cache;
                    }
                    const resultOfOriginalFn = originalFn(...args, memoizeRecursionMap);
                    return resultOfOriginalFn;
                };
            };
            // With the recursion schema function we get the Zod schema which we need to replace
            // with the predefined `z.custom` schemas.
            const recursionSchemasMap = recursionSchemas(memoize, ...args);
            s = s.omit(Object.keys(recursionSchemasMap).reduce((acc, key)=>{
                acc[key] = true;
                return acc;
            }, {})).extend(recursionSchemasMap);
            memoizeRecursionMap.set(identifier, s);
        }
        const m = middleware ? middleware(s) : s;
        const r = refine ? refine(m, ...args) : m;
        return r;
    };
    fn.identifier = identifier;
    return fn;
}
export { createRefinableSchema };
